import React, { useMemo } from 'react';
import { Link } from 'react-router';
import {
  Award,
  CalendarDays,
  CalendarRange,
  ChevronRight,
  History,
  Leaf,
  MessageSquare,
  RefreshCw,
  Timer
} from 'lucide-react';
import { topicOptions } from '../data/verseDeliveryContent';
import useDeliveryState from '../hooks/useDeliveryState';

const topicTitles = {
  anxious: 'Peace for anxious thoughts',
  grateful: 'Gratitude for today',
  strength: 'Strength for what is ahead',
  grief: 'Comfort while you grieve',
  guidance: 'Wisdom for the way ahead',
  hope: 'Hope for what comes next',
  love: 'Grace for your relationships',
  morning: 'A grounded start to the day',
  night: 'Rest for the evening'
};

const channelName = (medium = 'email') => ({ email: 'Email', slack: 'Slack' }[medium] || medium);

const formatWhen = (value) => {
  if (!value) return 'Not scheduled yet';
  const date = new Date(value);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const sameDay = (left, right) => left.toDateString() === right.toDateString();
  const day = sameDay(date, today)
    ? 'Today'
    : sameDay(date, tomorrow)
      ? 'Tomorrow'
      : new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
  return `${day} ${time}`;
};

const dayKey = (value) => new Date(value).toDateString();

// A streak is consecutive calendar days ending today or yesterday that have at
// least one delivered verse — the same rule the merits badges assume.
const currentStreak = (deliveries) => {
  const days = new Set(deliveries
    .filter((delivery) => delivery.deliveredAt || delivery.openedAt || delivery.status === 'delivered')
    .map((delivery) => dayKey(delivery.deliveredAt || delivery.openedAt || delivery.scheduledAt)));
  if (!days.size) return 0;

  const cursor = new Date();
  if (!days.has(cursor.toDateString())) cursor.setDate(cursor.getDate() - 1);

  let streak = 0;
  while (days.has(cursor.toDateString())) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
};

const rows = [
  ['Verse schedules', 'Manage optional email delivery schedules.', '/prayer/schedule', CalendarDays],
  ['Manage deliveries', 'Review queued verses, skip a delivery, or pause a schedule.', '/practice/deliveries', CalendarDays],
  ['Delivery history', 'Open your complete delivery record and channel filters.', '/practice/history', History],
  ['Journal', 'Reflect on today’s reading or what is on your heart.', '/practice/journal', MessageSquare],
  ['Prayer requests', 'Hold the needs you are carrying and mark the answered ones.', '/practice/requests', Leaf],
  ['Prayer Garden', 'Tend your prayers and watch what grows.', '/practice/garden', Leaf],
  ['Prayer calendar', 'Plan a prayer path and export it to your own calendar.', '/practice/prayer-calendar', CalendarRange],
  ['Prayer timer', 'Create stillness for a set stretch of time.', '/practice/timer', Timer],
  ['Merits & growth', 'See the playful merits tied to your completed activity.', '/practice/merits', Award]
];

export default function PracticeDashboard() {
  const { subscriptions, deliveries, loading, error, reload } = useDeliveryState();

  const active = useMemo(
    () => subscriptions.filter((item) => item.status === 'active'),
    [subscriptions]
  );

  const next = useMemo(() => {
    const now = new Date();
    const activeIds = new Set(active.map((item) => item.id));
    return deliveries
      .filter((delivery) => activeIds.has(delivery.subscriptionId)
        && delivery.status === 'scheduled'
        && new Date(delivery.scheduledAt) >= now)
      .sort((left, right) => new Date(left.scheduledAt) - new Date(right.scheduledAt))[0];
  }, [active, deliveries]);

  const subscription = subscriptions.find((item) => item.id === next?.subscriptionId) || active[0];
  const streak = useMemo(() => currentStreak(deliveries), [deliveries]);

  return (
    <div className="vd-shell vd-launcher-shell">
      <main className="vd-launcher vd-practice">
        <p className="vd-launcher-eyebrow">My Practice</p>
        <h1>Your week with the Word</h1>
        <p className="vd-launcher-sub">Your journal, prayer tools, and optional verse deliveries.</p>

        {loading ? (
          <p className="vd-launcher-glimpse-state" role="status">Loading your practice…</p>
        ) : error ? (
          <p className="vd-launcher-glimpse-state" role="alert">
            {error}
            <button type="button" onClick={() => reload().catch(() => undefined)}>
              <RefreshCw size={15} aria-hidden="true" /> Try again
            </button>
          </p>
        ) : (
          <>
            {next ? (
              <section className="vd-practice-next" aria-labelledby="vd-practice-next-heading">
                <p className="vd-practice-next-label">Next delivery · {formatWhen(next.scheduledAt)}</p>
                <h2 id="vd-practice-next-heading">“{next.content?.text}”</h2>
                <p className="vd-practice-next-meta">
                  {topicTitles[subscription?.topic]
                    || topicOptions.find((topic) => topic.id === subscription?.topic)?.label
                    || 'Scripture for your day'}
                  {' · '}{next.content?.reference}
                  {' · '}{channelName(subscription?.medium)}
                </p>
              </section>
            ) : (
              <section className="vd-practice-next is-empty">
                <p className="vd-practice-next-label">Your daily practice</p>
                <h2>Today’s readings are ready whenever you are.</h2>
                <Link to="/">Open today’s readings <ChevronRight size={16} aria-hidden="true" /></Link>
              </section>
            )}

            <div className="vd-practice-stats">
              <div className="vd-practice-stat">
                <strong>{streak}<em> {streak === 1 ? 'day' : 'days'}</em></strong>
                <span>Current streak</span>
              </div>
              <div className="vd-practice-stat">
                <strong>{active.length}</strong>
                <span>Active schedules</span>
              </div>
            </div>
          </>
        )}

        <nav className="vd-practice-rows" aria-label="Practice tools">
          {rows.map(([title, body, to, Icon]) => (
            <Link to={to} key={to}>
              <span className="vd-practice-row-icon"><Icon size={20} aria-hidden="true" /></span>
              <span className="vd-practice-row-copy">
                <strong>{title}</strong>
                <small>{body}</small>
              </span>
              <ChevronRight size={18} aria-hidden="true" />
            </Link>
          ))}
        </nav>
      </main>
    </div>
  );
}
